import React from "react"
import { Link } from "gatsby"
import styled from "styled-components/macro"
import colors from "../scripts/colors"
import "@fontsource/noto-sans-jp/300.css"
import "@fontsource/raleway/300.css"
import "@fontsource/raleway/500.css"
import PageLayout from "../components/PageLayout"
import PageTitle from "../components/PageTitle/PageTitle"
import { PageMargin } from "../GlobalStyles"
import { camelCase } from "camel-case"
import { paramCase } from "param-case"
import ListArrow from "../images/iconmonstr-arrow-24.svg"
import PhoneIcon from "../images/iconmonstr-phone-6.svg"
import MoreInfoContact from "../components/MoreInfo/MoreInfoContact"
import ContactBar from "../components/ContactBars/ContactBar"
import Button from "../components/Button/Button"
import ChevronRight from "../images/iconmonstr-arrow-24.svg"
import ChevronOLDown from "../images/iconmonstr-arrow-24---outline.svg"
import ServiceNav from "../components/Service/ServiceNav"
import { decode } from "js-base64"
import { Helmet } from "react-helmet"
import useFacilityData from "../hooks/useFacilityData.ts"
import PortableText from "react-portable-text"
import {
  ULStyles,
  OLStyles,
  LIStyles,
  LiArrow,
  ListItem,
} from "../components/Article/ArticleContent"

const bgImgUrl = (imgId, width, height, gravity = "auto") =>
  `https://res.cloudinary.com/agoraservices/g_${gravity},c_fill,w_${width},h_${height},q_auto,f_auto/${imgId}`

const ServicePageMargin = styled(PageMargin)`
  #ServicePage-contactBox {
    .ContactBar {
      margin-left: 10px;
    }
    .Button {
      margin-left: 25px;
    }
  }
`

const PageContentWrap = styled.section`
  display: flex;
  flex-direction: column;
  margin-top: 100px;
  @media screen and (min-width: 790px) {
    flex-direction: row;
  }
`

const Image = styled.div`
  background-image: ${(props: any) =>
    `url('https://res.cloudinary.com/agoraservices/f_auto,q_auto,c_fill,w_650,h_625,g_auto/${props.bannerId}')`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  height: 400px;
  border: 1px solid ${colors("grey", "dk")};
  flex-shrink: 0;
  @media screen and (min-width: 790px) {
    width: 400px;
    height: 400px;
    margin-bottom: 85px;
  }
  @media screen and (min-width: 1500px) {
    width: 650px;
    height: 625px;
  }
`

const ContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 85px;
  align-items: center;
  @media screen and (min-width: 790px) {
    align-items: flex-start;
  }
  .PortableText {
    margin: 45px 0 0;
    @media screen and (min-width: 600px) {
      margin: 45px 0 0 45px;
    }
    .LiArrow {
      margin-top: 0.27rem;
    }
  }
`

const ContentPurpleBox = styled.div`
  width: 120px;
  height: 80px;
  background-color: ${colors("purple", "", 0.75)};
  margin-top: -100px;
  margin-left: 0;
  backdrop-filter: blur(4px);
  position: relative;
  z-index: 100;
  @media screen and (min-width: 790px) {
    margin-top: -25px;
    margin-left: -90px;
  }
`

const DescriptionBox = styled.div`
  background-color: ${colors("grey", "dk", 0.75)};
  backdrop-filter: blur(4px);
  padding: 45px;
  padding-top: 75px;
  padding-left: 45;
  font-size: 20px;
  line-height: 1.65em;
  color: white;
  margin-top: -30px;
  margin-left: 0;
  width: ${(props: any) => (props.children ? "75%" : "150px")};
  @media screen and (min-width: 790px) {
    padding-left: 102px;
    margin-left: -57px;
    width: ${(props: any) => (props.children ? "auto" : "25px")};
  }
  @media screen and (min-width: 1500px) {
    font-size: 24px;
  }
`

const FeatureList = styled.ul`
  padding: 0;
  margin: 45px 0 0;
  list-style-type: none;
  width: 100%;
  display: flex;
  /* justify-content: center; */
  flex-wrap: wrap;
  @media screen and (min-width: 790px) {
    display: block;
    margin: 45px 0 0 45px;
    width: auto;
  }
`

const BlockList = styled(FeatureList)``

const BlockStyles = styled.div``

const BlockTitle = styled.h3`
  margin: 0 0 10px;
`

const BlockFeatureUl = styled(ULStyles)`
  margin: 0 0 30px;
`

const FeatureItemStyles = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 13px;
  margin-right: 20px;
  svg {
    height: 13px;
    color: ${colors("purple")};
    margin-top: 7px;
  }
  @media screen and (min-width: 790px) {
    margin-right: 0;
  }
`

const FeatureItemText = styled.div`
  font-size: 18px;
  /* font-weight: 300; */
  line-height: 25px;
`

const PortableTextH3 = styled.h3`
  margin: 0 0 20px;
`

const PortableTextUl = styled(ULStyles)`
  margin: 0 0 30px;
`

const FeatureItem = ({ children }) => {
  return (
    <FeatureItemStyles>
      <ListArrow />
      <FeatureItemText>{children}</FeatureItemText>
    </FeatureItemStyles>
  )
}

const ServicePage = ({ pageContext }) => {
  const facilityData = useFacilityData()

  const facilityInfo = facilityData.sanityFacility.facilityObj

  const serviceList = pageContext.serviceItemList.map(serviceItem => {
    return (
      <FeatureItem
        key={camelCase(serviceItem)}
        itemProp="amenityFeature"
        itemscope
        itemtype="https://schema.org/LocationFeatureSpecification"
      >
        <span itemProp="name">{serviceItem}</span>
        <meta itemProp="value" content="True" />
      </FeatureItem>
    )
  })

  const serviceBlockList = pageContext.serviceBlocks.map(block => {
    // console.log(block)

    const blockFeatureList = block.features.map(feature => (
      <FeatureItemStyles key={camelCase(feature)}>
        <ListArrow />
        <FeatureItemText>{feature}</FeatureItemText>
      </FeatureItemStyles>
    ))

    return (
      <BlockStyles key={block._key}>
        <BlockTitle>{block.title}</BlockTitle>
        <BlockFeatureUl>{blockFeatureList}</BlockFeatureUl>
      </BlockStyles>
    )
  })

  const bannerId = pageContext.bannerImage
    ? JSON.parse(decode(pageContext.bannerImage.asset.source.id)).public_id
    : "/Priority Health Facilities/General Images/iStock-1225199427---W_wax6yx.jpg"

  const metaTitle = `${pageContext.title} < ${facilityData.sanityFacility.facilityObj.facilityName}`

  const siteUrl = facilityData.site.siteMetadata.siteUrl

  const url = `${siteUrl}/${paramCase(pageContext.title)}`

  const metaDesc = pageContext.description
    ? pageContext.description
    : facilityInfo.siteContent.initParagraphText

  return (
    <PageLayout currLocation={`/${pageContext.slug}`}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <link rel="canonical" href={url} />
        <meta name="description" content={metaDesc} />

        <meta property="og:url" content={url} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />

        <meta property="twitter:url" content={url} />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDesc} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@id": siteUrl,
                  name: "Home",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@id": url,
                  name: pageContext.title,
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <ServicePageMargin>
        <article role="article">
          <header role="banner">
            <PageTitle>{pageContext.title}</PageTitle>
          </header>
          <PageContentWrap>
            <aside role="complementary">
              <Image bannerId={bannerId}></Image>
            </aside>
            <section role="region">
              <ContentGroup>
                <ContentPurpleBox />
                <DescriptionBox>{pageContext.description}</DescriptionBox>
                {serviceBlockList.length > 0 && (
                  <BlockList as="div">{serviceBlockList}</BlockList>
                )}
                {serviceList.length > 0 && (
                  <FeatureList>{serviceList}</FeatureList>
                )}
              </ContentGroup>
            </section>
          </PageContentWrap>
          <footer role="contentinfo">
            <MoreInfoContact />
            <ServiceNav currTitle={pageContext.title} />
          </footer>
        </article>
      </ServicePageMargin>
    </PageLayout>
  )
}

export default ServicePage
